<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import {
	EEntidadFederativa,
	EFundamentoServicioSocial,
	EGraduationMode,
	ETipoEstudioAntecedente,
	IDegree,
	NOMBRE_ENTIDAD_FEDERATIVA,
	NOMBRE_FUNDAMENTO_SERVICIO_SOCIAL,
	NOMBRE_GRADUATION_MODE,
	NOMBRE_TIPO_ESTUDIO_ANTECEDENTE,
	NOMBRE_AUTORIZACION,
	EAuthorizationType,
	ENivelEstudio
} from '@shared/types'
import { CDegree } from '@/constants/CDegree'
import dateControl from '../utils/dateControl.vue'
import { ICareerResponse, EIdNivelEstudios, CIdNivelEstudios } from '@shared/types/base'
import { degreeService } from '@/services/degreeService'
import { VaProgressCircle } from 'vuestic-ui/web-components'
import { esFechaValida } from '@/utils/validations'
import { verifyCurp } from '@shared/modules/curpUtilities.service'
import { IPerson } from '@shared/modules/curpUtilities.types'
import { getInstitutionConfiguration, normalizarFecha } from '@/components/utils/functions'

const props = defineProps<{
	modelValue: boolean
	institution: any
	degree?: IDegree
	isTestMode: boolean
	currentInstitution: string
	isEdit: boolean
}>()

const emit = defineEmits<{
	'update:modelValue': [value: boolean]
	'degree-saved': [degree: IDegree]
	'degree-updated': [degree: IDegree]
}>()

const showModal = computed({
	get: () => props.modelValue,
	set: value => emit('update:modelValue', value)
})

const TABS = [
	{ icon: 'person', title: 'Profesionista' },
	{ icon: 'school', title: 'Título' }
]
const value = ref(TABS[0].title)
const institutionName = ref('')
const currentTab = computed(() => TABS.find(({ title }) => title === value.value))
const showModalLoading = ref(false)
const carreras = computed<ICareerResponse[]>(() => props.institution.Carrera.filter((c: ICareerResponse) => c && typeof c.idCarrera !== 'undefined'))
const idCarrera = ref<string | null>(null)
const idTipoEstudioAntecedente = ref<ETipoEstudioAntecedente | null>(null)
const idEntidadFederativa = ref<EEntidadFederativa | null>(null)
const idModalidadTitulacion = ref<EGraduationMode | null>(null)
const idFundamentoLegalServicioSocial = ref<EFundamentoServicioSocial | null>(null)
const idEntidadFederativaExpedicion = ref<EEntidadFederativa | null>(null)
const errorsModal = ref<string[]>([])
const degreeData = ref<IDegree>(JSON.parse(JSON.stringify(CDegree)))
const nivelEstudios = [ENivelEstudio.DOCTORADO, ENivelEstudio.ESPECIALIDAD, ENivelEstudio.MAESTRIA]
const isProfesionistaValid = ref(false)
const selectedResponsables = ref<string[]>([])

const opcionesServicioSocial = [
	{ label: 'Si', value: 1 },
	{ label: 'No', value: 0 }
]

// Función para inicializar los datos del formulario
const initializeFormData = (degree?: IDegree) => {
	try {
		if (degree && typeof degree === 'object' && degree.data) {
			degreeData.value = JSON.parse(JSON.stringify(degree))
			idCarrera.value = degree.data.Carrera?.idCarrera ?? null
			idTipoEstudioAntecedente.value = degree.data.Antecedente?.idTipoEstudioAntecedente ?? null
			idEntidadFederativa.value = degree.data.Antecedente?.idEntidadFederativa ?? null
			idModalidadTitulacion.value = degree.data.Expedicion?.idModalidadTitulacion ?? null
			idFundamentoLegalServicioSocial.value = degree.data.Expedicion?.idFundamentoLegalServicioSocial ?? null
			idEntidadFederativaExpedicion.value = degree.data.Expedicion?.idEntidadFederativa ?? null
			selectedResponsables.value = Object.keys(degree.data.FirmaResponsables || {})
		} else {
			degreeData.value = JSON.parse(JSON.stringify(CDegree))
			idCarrera.value = null
			idTipoEstudioAntecedente.value = null
			idEntidadFederativa.value = null
			idModalidadTitulacion.value = null
			idFundamentoLegalServicioSocial.value = null
			idEntidadFederativaExpedicion.value = null
			selectedResponsables.value = []
		}
		errorsModal.value = []
		value.value = TABS[0].title
	} catch (error) {
		console.error('Error inicializando datos del formulario:', error)
		// Fallback a datos por defecto
		degreeData.value = JSON.parse(JSON.stringify(CDegree))
		selectedResponsables.value = []
		errorsModal.value = []
		value.value = TABS[0].title
	}
}

/**
 * @description Valida los datos del profesionista
 * @returns {boolean} true si hay errores, false si está correcto
 */
const validarDatosProfesionista = (): boolean => {
	const datosProfesionista = [
		degreeData.value.data.numeroControl?.trim(),
		degreeData.value.data.Profesionista.curp?.trim(),
		degreeData.value.data.Profesionista.nombre?.trim(),
		degreeData.value.data.Profesionista.primerApellido?.trim(),
		degreeData.value.data.Profesionista.correoElectronico?.trim()
	]

	if (datosProfesionista.some(dato => !dato)) {
		return true
	}
	return false
}

/**
 * @description Valida los datos de antecedentes
 * @returns {boolean} true si hay errores, false si está correcto
 */
const validarDatosAntecedentes = (): boolean => {
	const datosAntecedentes = [
		idTipoEstudioAntecedente.value,
		idEntidadFederativa.value,
		degreeData.value.data.Antecedente.fechaTerminacion,
		degreeData.value.data.Antecedente.institucionProcedencia?.trim()
	]

	if (datosAntecedentes.some(dato => !dato)) {
		return true
	}

	// Validar cédula para maestría
	if (
		(idTipoEstudioAntecedente.value === ETipoEstudioAntecedente.LICENCIATURA || idTipoEstudioAntecedente.value === ETipoEstudioAntecedente.TECNICO_SUPERIOR_UNIVERSITARIO) &&
		!degreeData.value.data.Antecedente.noCedula?.trim()
	) {
		return true
	}
	return false
}

/**
 * @description Valida los datos de la carrera
 * @returns {boolean} true si hay errores, false si está correcto
 */
const validarDatosCarrera = (): boolean => {
	const datosCarrera = [idCarrera.value, degreeData.value.data.Carrera.numeroRvoe, degreeData.value.data.Carrera.fechaInicio, degreeData.value.data.Carrera.fechaTerminacion]

	if (datosCarrera.some(dato => !dato)) {
		return true
	}
	return false
}

/**
 * @description Valida los datos de expedición
 * @returns {boolean} true si hay errores, false si está correcto
 */
const validarDatosExpedicion = (): boolean => {
	const datosExpedicion = [
		degreeData.value.data.Expedicion.fechaExpedicion,
		idEntidadFederativaExpedicion.value,
		idModalidadTitulacion.value,
		degreeData.value.data.Expedicion.idModalidadTitulacion === EGraduationMode.POR_TESIS
			? degreeData.value.data.Expedicion.fechaExamenProfesional
			: degreeData.value.data.Expedicion.fechaExencionExamenProfesional
	]

	if (datosExpedicion.some(dato => !dato)) {
		return true
	}
	return false
}

/**
 * @description Valida el servicio social
 */
const validarServicioSocial = (): void => {
	// Solo validar para niveles que no son doctorado, especialidad o maestría
	if (!nivelEstudios.includes(degreeData.value.data.Carrera.idNivelEstudios)) {
		if (degreeData.value.data.Expedicion.cumplioServicioSocial === null || degreeData.value.data.Expedicion.cumplioServicioSocial === undefined) {
			errorsModal.value.push('El campo "Cumplio Servicio Social" es requerido')
			return
		}

		if (degreeData.value.data.Expedicion.cumplioServicioSocial === 1 && (!idFundamentoLegalServicioSocial.value || idFundamentoLegalServicioSocial.value === EFundamentoServicioSocial.NO_APLICA)) {
			errorsModal.value.push('El campo "Fundamento Legal del Servicio Social" es requerido y no puede ser "No Aplica" si el campo "Cumplio Servicio Social" es "Si"')
			return
		}
		if (degreeData.value.data.Expedicion.cumplioServicioSocial === 0 && idFundamentoLegalServicioSocial.value !== EFundamentoServicioSocial.NO_APLICA) {
			errorsModal.value.push('El campo "Fundamento Legal del Servicio Social" no puede ser otro si el campo "Cumplio Servicio Social" es "No"')
			return
		}
	}
	errorsModal.value = errorsModal.value.filter(error => !error.includes('El campo "Cumplio Servicio Social" es requerido'))
	errorsModal.value = errorsModal.value.filter(
		error => !error.includes('El campo "Fundamento Legal del Servicio Social" es requerido y no puede ser "No Aplica" si el campo "Cumplio Servicio Social" es "Si"')
	)
	errorsModal.value = errorsModal.value.filter(error => !error.includes('El campo "Fundamento Legal del Servicio Social" no puede ser otro si el campo "Cumplio Servicio Social" es "No"'))
}

/**
 * @description Valida los responsables seleccionados
 * @returns {boolean} true si hay errores, false si está correcto
 */
const validarResponsables = (): boolean => {
	if (selectedResponsables.value.length === 0) {
		return true
	}
	return false
}

/**
 * @description Función que valida que todos los campos requeridos del formulario estén completos
 * @returns {boolean} true si todos los campos están completos, false en caso contrario
 */
const validarFormularioCompleto = () => {
	// Obtener errores de cada sección
	const erroresProfesionista = validarDatosProfesionista()
	const erroresAntecedentes = validarDatosAntecedentes()
	const erroresCarrera = validarDatosCarrera()
	const erroresExpedicion = validarDatosExpedicion()
	const erroresResponsables = validarResponsables()

	isProfesionistaValid.value = !erroresProfesionista && !erroresAntecedentes && !erroresCarrera

	// Filtrar solo el error de "Todos los campos son requeridos" sin afectar otros errores específicos
	errorsModal.value = errorsModal.value.filter(error => error !== 'Todos los campos son requeridos')

	if (!erroresProfesionista && !erroresAntecedentes && !erroresCarrera && !erroresExpedicion && !erroresResponsables) {
		// Solo limpiar errores si no hay errores de validación específicos
		if (errorsModal.value.length === 0) {
			errorsModal.value = []
		}
	} else {
		const errorMessage = 'Todos los campos son requeridos'
		if (!errorsModal.value.includes(errorMessage)) {
			errorsModal.value.push(errorMessage)
		}
	}
}

// Watcher para props.degree con immediate: true
watch(
	() => props.degree,
	async newVal => {
		initializeFormData(newVal)
		institutionName.value = props.institution?.nombreInstitucion || (await degreeService.getInstitutionName(props.currentInstitution))
	},
	{ immediate: true, deep: true }
)
const getCurpError = (curp: string) => {
	if (curp.toUpperCase() === 'EXTRANJERO') {
		errorsModal.value = errorsModal.value.filter(error => !error.includes('CURP'))
		idEntidadFederativa.value = EEntidadFederativa.EXTRANJERO
		return
	}
	errorsModal.value = errorsModal.value.filter(error => !error.includes('CURP'))
	const person: IPerson = {
		name: degreeData.value.data.Profesionista.nombre,
		firstLastName: degreeData.value.data.Profesionista.primerApellido,
		secondLastName: degreeData.value.data.Profesionista.segundoApellido
	}
	if (verifyCurp(curp, person).code === 2) {
		errorsModal.value.push('La CURP no coincide con los datos del estudiante')
	} else {
		errorsModal.value = errorsModal.value.filter(error => !error.includes('CURP'))
	}
}
// Watcher para showModal para reinicializar cuando se abre
watch(
	() => showModal.value,
	async isOpen => {
		if (isOpen) {
			initializeFormData(props.degree)
			institutionName.value = props.institution?.nombreInstitucion || (await degreeService.getInstitutionName(props.currentInstitution))
			// Solo validar campos si es modo edición
			if (props.isEdit) {
				validarFormularioCompleto()
				getCurpError(degreeData.value.data.Profesionista.curp)
				getMatriculaError(degreeData.value.data.numeroControl)
				getCorreoElectronicoError(degreeData.value.data.Profesionista.correoElectronico)
			} else {
				console.log('Modo creación, no se ejecutan validaciones')
			}
		}
	},
	{ immediate: true }
)

onMounted(async () => {
	// Asegurar que los datos se inicialicen correctamente
	initializeFormData(props.degree)
	institutionName.value = props.institution?.nombreInstitucion || (await degreeService.getInstitutionName(props.currentInstitution))

	// Si el modal está abierto y es modo edición, ejecutar validaciones
	if (showModal.value && props.isEdit) {
		validarFormularioCompleto()
		getCurpError(degreeData.value.data.Profesionista.curp)
		getMatriculaError(degreeData.value.data.numeroControl)
		getCorreoElectronicoError(degreeData.value.data.Profesionista.correoElectronico)
	}
})

const getCveCarrera = () => {
	if (!degreeData.value.data.Carrera.idCarrera || !Array.isArray(props.institution.Carrera)) return ''
	const carrerasValidas = props.institution.Carrera.filter((c: any) => c && typeof c.idCarrera !== 'undefined' && c.Revoes)
	const carreraSeleccionada = carrerasValidas.find((c: any) => c.idCarrera === degreeData.value.data.Carrera.idCarrera)
	if (!carreraSeleccionada) return ''
	return carreraSeleccionada.claveCarrera
}

onUnmounted(() => {
	// Limpiar datos al desmontar
	degreeData.value = JSON.parse(JSON.stringify(CDegree))
	selectedResponsables.value = []
})

/**
 * @description Computed property que obtiene las opciones de entidad federativa
 * @type {ComputedRef<Array<{value: EEntidadFederativa, text: string}>>}
 */
const opcionesEntidadFederativa = computed(() => {
	let valores = [{ value: EEntidadFederativa.MEXICO, text: NOMBRE_ENTIDAD_FEDERATIVA[EEntidadFederativa.MEXICO] }]
	Object.values(EEntidadFederativa).forEach(v => {
		if (v !== EEntidadFederativa.MEXICO) {
			valores.push({ value: v, text: NOMBRE_ENTIDAD_FEDERATIVA[v] })
		}
	})
	return valores
})
const opcionesTipoEstudioAntecedente = computed(() => {
	let valores = [{ value: ETipoEstudioAntecedente.LICENCIATURA, text: NOMBRE_TIPO_ESTUDIO_ANTECEDENTE[ETipoEstudioAntecedente.LICENCIATURA] }]
	Object.values(ETipoEstudioAntecedente).forEach(v => {
		if (v !== ETipoEstudioAntecedente.LICENCIATURA) {
			valores.push({ value: v, text: NOMBRE_TIPO_ESTUDIO_ANTECEDENTE[v] })
		}
	})
	return valores
})
const opcionesModalidadTitulacion = computed(() => {
	let valores = [{ value: EGraduationMode.OTRO, text: NOMBRE_GRADUATION_MODE[EGraduationMode.OTRO] }]
	Object.values(EGraduationMode).forEach(v => {
		if (v !== EGraduationMode.OTRO) {
			valores.push({ value: v, text: NOMBRE_GRADUATION_MODE[v] })
		}
	})
	return valores
})

const opcionesFundamentoLegalServicioSocial = computed(() => {
	let valores = [{ value: EFundamentoServicioSocial.ART_52_LR_ART_5_CONST, text: NOMBRE_FUNDAMENTO_SERVICIO_SOCIAL[EFundamentoServicioSocial.ART_52_LR_ART_5_CONST] }]
	Object.entries(EFundamentoServicioSocial).forEach(([key, value]) => {
		if (key !== 'ART_52_LR_ART_5_CONST' && typeof value === 'number') {
			valores.push({ value: value, text: NOMBRE_FUNDAMENTO_SERVICIO_SOCIAL[value] })
		}
	})
	return valores
})
watch(
	() => idCarrera.value,
	(newValue, oldValue) => {
		if (newValue !== oldValue) {
			if (!newValue || !Array.isArray(carreras.value) || carreras.value.length === 0) return

			const carrerasValidas = carreras.value.filter(c => c && typeof c.idCarrera !== 'undefined')
			const carreraSeleccionada = carrerasValidas.find(c => c.idCarrera === newValue)
			if (!carreraSeleccionada) return
			degreeData.value.data.Carrera.idCarrera = newValue
			const nuevosRevoes = revoCurrentCarrer.value.map(opt => opt.value)
			if (!nuevosRevoes.includes(degreeData.value.data.Carrera.numeroRvoe)) {
				degreeData.value.data.Carrera.numeroRvoe = nuevosRevoes[0] || ''
			}
			degreeData.value.data.Carrera.nombreCarrera = carreraSeleccionada.nombreCarrera || ''
			degreeData.value.data.Carrera.cveCarrera = carreraSeleccionada.claveCarrera || ''
			degreeData.value.data.Carrera.idNivelEstudios = carreraSeleccionada.idNivelEstudios || 0
			const revoeSeleccionado = carreraSeleccionada.Revoes[degreeData.value.data.Carrera.numeroRvoe]
			if (!revoeSeleccionado) return
			degreeData.value.data.Carrera.idAutorizacionReconocimiento = revoeSeleccionado.idAutorizacionReconocimiento
			degreeData.value.data.Carrera.autorizacionReconocimiento = NOMBRE_AUTORIZACION[revoeSeleccionado.idAutorizacionReconocimiento as EAuthorizationType]
			degreeData.value.data.Carrera.calificacionMaxima = revoeSeleccionado.calificacionMaxima
			degreeData.value.data.Carrera.calificacionMinima = revoeSeleccionado.calificacionMinima
			degreeData.value.data.Carrera.calificacionMinimaAprobatoria = revoeSeleccionado.calificacionMinimaAprobatoria
		}
	}
)
watch(
	() => idFundamentoLegalServicioSocial.value,
	newValue => {
		let val = newValue
		if (typeof val === 'string') {
			val = Number(val)
			idFundamentoLegalServicioSocial.value = val
		}
		degreeData.value.data.Expedicion.idFundamentoLegalServicioSocial = idFundamentoLegalServicioSocial.value as EFundamentoServicioSocial
		degreeData.value.data.Expedicion.fundamentoLegalServicioSocial = NOMBRE_FUNDAMENTO_SERVICIO_SOCIAL[val as EFundamentoServicioSocial]
		// Validar formulario
		validarServicioSocial()
		validarFormularioCompleto()
	}
)
watch(
	() => idModalidadTitulacion.value,
	(newValue, oldValue) => {
		if (newValue !== oldValue) {
			degreeData.value.data.Expedicion.idModalidadTitulacion = newValue as EGraduationMode
			degreeData.value.data.Expedicion.modalidadTitulacion = NOMBRE_GRADUATION_MODE[newValue as EGraduationMode]
			if (newValue === EGraduationMode.POR_TESIS) {
				degreeData.value.data.Expedicion.fechaExencionExamenProfesional = ''
				errorsModal.value = errorsModal.value.filter(error => !error.includes('exención del examen profesional'))
			} else {
				degreeData.value.data.Expedicion.fechaExamenProfesional = ''
				errorsModal.value = errorsModal.value.filter(error => !error.includes('La fecha del examen profesional'))
			}
		}
		// Validar formulario
		validarFormularioCompleto()
	}
)
watch(
	() => idEntidadFederativaExpedicion.value,
	(newValue, oldValue) => {
		if (newValue !== oldValue) {
			degreeData.value.data.Expedicion.idEntidadFederativa = newValue as EEntidadFederativa
			degreeData.value.data.Expedicion.entidadFederativa = NOMBRE_ENTIDAD_FEDERATIVA[newValue as EEntidadFederativa]
		}
		// Validar formulario
		validarFormularioCompleto()
	}
)
watch(
	() => idEntidadFederativa.value,
	(newValue, oldValue) => {
		if (newValue !== oldValue) {
			degreeData.value.data.Antecedente.idEntidadFederativa = newValue as EEntidadFederativa
			degreeData.value.data.Antecedente.entidadFederativa = NOMBRE_ENTIDAD_FEDERATIVA[newValue as EEntidadFederativa]
		}
		// Validar formulario
		validarFormularioCompleto()
	}
)
watch(
	() => idTipoEstudioAntecedente.value,
	(newValue, oldValue) => {
		if (newValue !== oldValue) {
			degreeData.value.data.Antecedente.idTipoEstudioAntecedente = newValue as ETipoEstudioAntecedente
			degreeData.value.data.Antecedente.tipoEstudioAntecedente = NOMBRE_TIPO_ESTUDIO_ANTECEDENTE[newValue as ETipoEstudioAntecedente]
		}
		// Validar formulario
		validarFormularioCompleto()
	}
)
/**
 * @description Computed property que obtiene los RVOE de la carrera actual
 * @type {ComputedRef<Array<{value: string, text: string}>>}
 */
const revoCurrentCarrer = computed(() => {
	if (!degreeData.value.data.Carrera.idCarrera || !Array.isArray(props.institution.Carrera)) return []
	// Filtrar solo carreras válidas
	const carrerasValidas = props.institution.Carrera.filter((c: any) => c && typeof c.idCarrera !== 'undefined' && c.Revoes)
	const carreraSeleccionada = carrerasValidas.find((c: any) => c.idCarrera === degreeData.value.data.Carrera.idCarrera)
	if (!carreraSeleccionada) return []
	return Object.entries(carreraSeleccionada.Revoes).map(([key, value]: [string, any]) => ({
		value: key,
		text: value.numero
	}))
})
/**
 * @description Computed property que obtiene las opciones de responsable
 * @type {ComputedRef<Array<{value: string, text: string}>>}
 */
const opcionesResponsable = computed(() => {
	const responsables = Object.values(props.institution?.Responsable ?? {}).filter((r: any) => {
		const degrees = r?.degrees
		// Solo responsables que sí tienen el nodo `degrees` (no null/undefined) y además no está vacío
		if (degrees == null) return false
		if (Array.isArray(degrees)) return degrees.length > 0
		if (typeof degrees === 'object') return Object.keys(degrees).length > 0
		return Boolean(degrees)
	})
	return responsables.map((r: any) => ({
		value: r.curp,
		text: r.nombre + ' ' + r.primerApellido + ' ' + r.segundoApellido
	}))
})
const closeModal = () => {
	showModal.value = false
}

/**
 * @description Función para manejar la selección de responsables
 * @param responsableId - ID del responsable a seleccionar/deseleccionar
 */
const toggleResponsable = (responsableId: string) => {
	const index = selectedResponsables.value.indexOf(responsableId)
	if (index > -1) {
		selectedResponsables.value.splice(index, 1)
	} else {
		selectedResponsables.value.push(responsableId)
	}
}

/**
 * @description Función para verificar si un responsable está seleccionado
 * @param responsableId - ID del responsable a verificar
 * @returns boolean - true si está seleccionado, false en caso contrario
 */
const isResponsableSelected = (responsableId: string): boolean => selectedResponsables.value.includes(responsableId)

// Funciones de validación
const validAllDates = (fechaInicio: string, fechaTerminacionCarrera: string, fechaTerminacionAntecedente: string, fechaExpedicion: string, fechaExencionExamenProfesional: string) => {
	if (fechaInicio !== '' && fechaTerminacionCarrera !== '' && fechaTerminacionAntecedente !== '' && fechaExpedicion !== '' && fechaExencionExamenProfesional !== '') {
		errorsModal.value = errorsModal.value.filter(error => !error.includes('Todas las fechas son requeridas'))
		return true
	}
	errorsModal.value.push('Todas las fechas son requeridas')
	return false
}
// Funciones de manejo de fechas y validaciones
const getMatriculaError = (matricula: string) => {
	if (!matricula) return
	errorsModal.value = errorsModal.value.filter(error => !error.includes('matrícula'))
	if (matricula.includes('/')) {
		errorsModal.value.push('La matrícula no puede contener barras')
	} else {
		errorsModal.value = errorsModal.value.filter(error => !error.includes('matrícula'))
	}
}

const validarCedula = (value: string) => {
	if (!value) return
	errorsModal.value = errorsModal.value.filter(error => !error.includes('cédula'))
	if (value.length > 10) {
		errorsModal.value.push('La cédula debe tener menos de 10 dígitos')
	} else {
		errorsModal.value = errorsModal.value.filter(error => !error.includes('cédula'))
	}
	return true
}

const getCorreoElectronicoError = (correoElectronico: string) => {
	if (!correoElectronico) return
	errorsModal.value = errorsModal.value.filter(error => !error.includes('EMAIL'))
	const regex = /^[a-zA-Z0-9_]+([.][a-zA-Z0-9_]+)*@[a-zA-Z0-9_]+([.][a-zA-Z0-9_]+)*[.][a-zA-Z]{2,5}$/
	if (!regex.test(correoElectronico)) {
		const errorMessage = 'El email no es válido'
		if (!errorsModal.value.includes(errorMessage)) {
			errorsModal.value.push(errorMessage)
		}
	} else {
		errorsModal.value = errorsModal.value.filter(error => !error.includes('email'))
	}
}

// Funciones de manejo de fechas
const updateFechaTermino = (fecha: string) => {
	const fechaNormalizada = normalizarFecha(new Date(fecha).toISOString().split('T')[0])
	const fechaTerminacion = new Date(fechaNormalizada).setHours(0, 0, 0, 6)

	const fechaInicioCarrera = degreeData.value.data.Carrera.fechaInicio
		? new Date(normalizarFecha(new Date(degreeData.value.data.Carrera.fechaInicio).toISOString().split('T')[0])).setHours(0, 0, 0, 6)
		: new Date().setHours(0, 0, 0, 6)
	const errorMessage = 'La fecha de terminación de la carrera no puede ser anterior o igual a la fecha de inicio de la carrera'

	if (fechaTerminacion <= fechaInicioCarrera) {
		if (!errorsModal.value.includes(errorMessage)) {
			errorsModal.value.push(errorMessage)
		}
	} else {
		errorsModal.value = errorsModal.value.filter(error => !error.includes('terminación de la carrera'))
	}

	degreeData.value.data.Carrera.fechaTerminacion = fechaNormalizada
}

const updateFechaTerminoAntecedente = (fecha: string) => {
	const fechaNormalizada = normalizarFecha(new Date(fecha).toISOString().split('T')[0])
	const fechaTerminacion = new Date(fechaNormalizada).setHours(0, 0, 0, 6)
	const fechaInicioCarrera = degreeData.value.data.Carrera.fechaInicio
		? new Date(normalizarFecha(new Date(degreeData.value.data.Carrera.fechaInicio).toISOString().split('T')[0])).setHours(0, 0, 0, 6)
		: new Date().setHours(0, 0, 0, 6)
	if (fechaTerminacion >= fechaInicioCarrera) {
		const errorMessage = 'La fecha de terminación del antecedente no puede ser posterior o igual a la fecha de inicio de la carrera'
		if (!errorsModal.value.includes(errorMessage)) {
			errorsModal.value.push(errorMessage)
		}
	} else {
		errorsModal.value = errorsModal.value.filter(error => !error.includes('terminación del antecedente'))
	}
	degreeData.value.data.Antecedente.fechaTerminacion = new Date(normalizarFecha(fecha)).toISOString().split('T')[0]
}

const updateFechaInicio = (fecha: string) => {
	const fechaNormalizada = normalizarFecha(new Date(fecha).toISOString().split('T')[0])
	const fechaInicioDate = new Date(fechaNormalizada).setHours(0, 0, 0, 6)
	const fechaTerminacionCarreraDate = degreeData.value.data.Carrera.fechaTerminacion
		? new Date(normalizarFecha(new Date(degreeData.value.data.Carrera.fechaTerminacion).toISOString().split('T')[0])).setHours(0, 0, 0, 6)
		: new Date().setHours(0, 0, 0, 6)
	const fechaTerminacionAntecedenteDate = degreeData.value.data.Antecedente.fechaTerminacion
		? new Date(normalizarFecha(new Date(degreeData.value.data.Antecedente.fechaTerminacion).toISOString().split('T')[0])).setHours(0, 0, 0, 6)
		: new Date().setHours(0, 0, 0, 6)
	const errorCarrera = fechaTerminacionCarreraDate && fechaInicioDate >= fechaTerminacionCarreraDate
	const errorAntecedente = fechaTerminacionAntecedenteDate !== null && fechaInicioDate <= fechaTerminacionAntecedenteDate

	if (errorCarrera || errorAntecedente) {
		const errorMessage = 'La fecha de inicio de la carrera no puede ser posterior o igual a la fecha de terminación de la carrera ni anterior o igual a la fecha de terminación del antecedente'
		if (!errorsModal.value.includes(errorMessage)) {
			errorsModal.value.push(errorMessage)
		}
	} else {
		errorsModal.value = errorsModal.value.filter(error => !error.includes('inicio de la carrera'))
	}
	degreeData.value.data.Carrera.fechaInicio = fechaNormalizada
}

const updateFechaExpedicion = (value: any) => {
	if (new Date(value).setHours(0, 0, 0, 6) >= new Date().setHours(0, 0, 0, 6)) {
		const errorMessage = 'La fecha de expedición no puede ser hoy ni posterior'
		if (!errorsModal.value.includes(errorMessage)) {
			errorsModal.value.push(errorMessage)
		}
	} else {
		errorsModal.value = errorsModal.value.filter(error => !error.includes('La fecha de expedición no puede ser hoy ni posterior'))
	}
	degreeData.value.data.Expedicion.fechaExpedicion = new Date(value).toISOString().split('T')[0]
}

const updateFechaExencionExamenProfesional = (fecha: string) => {
	const fechaNormalizada = normalizarFecha(new Date(fecha).toISOString().split('T')[0])
	const fechaExencionExamenProfesional = new Date(fechaNormalizada).setHours(0, 0, 0, 6)
	const fechaExpedicion = degreeData.value.data.Expedicion.fechaExpedicion
		? new Date(normalizarFecha(new Date(degreeData.value.data.Expedicion.fechaExpedicion).toISOString().split('T')[0])).setHours(0, 0, 0, 6)
		: new Date().setHours(0, 0, 0, 6)
	const fechaTerminacionCarrera = degreeData.value.data.Carrera.fechaTerminacion
		? new Date(normalizarFecha(new Date(degreeData.value.data.Carrera.fechaTerminacion).toISOString().split('T')[0])).setHours(0, 0, 0, 6)
		: new Date().setHours(0, 0, 0, 6)

	const isValidDate = (d: Date | null) => d instanceof Date && !Number.isNaN(d.getTime())

	// Solo validar si ambas fechas existen y son válidas
	if (isValidDate(new Date(fechaNormalizada)) && isValidDate(new Date(fechaExpedicion)) && isValidDate(new Date(fechaTerminacionCarrera))) {
		if (fechaExencionExamenProfesional >= fechaExpedicion || fechaExencionExamenProfesional <= fechaTerminacionCarrera) {
			const errorMessage = 'La fecha de exención del examen profesional debe ser anterior a la fecha de expedición y posterior a la fecha de terminación de la carrera'
			if (!errorsModal.value.includes(errorMessage)) {
				errorsModal.value.push(errorMessage)
			}
		} else {
			errorsModal.value = errorsModal.value.filter(error => !error.includes('exención del examen profesional'))
		}
	} else {
		// Si alguna fecha no es válida, no mostrar el error de validación de exención
		errorsModal.value = errorsModal.value.filter(error => !error.includes('exención del examen profesional'))
	}
	degreeData.value.data.Expedicion.fechaExencionExamenProfesional = fechaNormalizada
}

const updateFechaExamenProfesional = (fecha: string) => {
	const fechaNormalizada = normalizarFecha(new Date(fecha).toISOString().split('T')[0])
	const fechaExamenProfesional = new Date(fechaNormalizada).setHours(0, 0, 0, 6)
	const fechaExpedicion = degreeData.value.data.Expedicion.fechaExpedicion
		? new Date(normalizarFecha(new Date(degreeData.value.data.Expedicion.fechaExpedicion).toISOString().split('T')[0])).setHours(0, 0, 0, 6)
		: new Date().setHours(0, 0, 0, 6)
	const fechaTerminacionCarrera = degreeData.value.data.Carrera.fechaTerminacion
		? new Date(normalizarFecha(new Date(degreeData.value.data.Carrera.fechaTerminacion).toISOString().split('T')[0])).setHours(0, 0, 0, 6)
		: new Date().setHours(0, 0, 0, 6)

	const isValidDate = (d: Date | null) => d instanceof Date && !Number.isNaN(d.getTime())

	// Solo validar si ambas fechas existen y son válidas
	if (isValidDate(new Date(fechaNormalizada)) && isValidDate(new Date(fechaExpedicion)) && isValidDate(new Date(fechaTerminacionCarrera))) {
		if (fechaExamenProfesional >= fechaExpedicion || fechaExamenProfesional <= fechaTerminacionCarrera) {
			const errorMessage = 'La fecha del examen profesional no puede ser posterior a la fecha de expedición ni anterior a la fecha de terminación de la carrera'
			if (!errorsModal.value.includes(errorMessage)) {
				errorsModal.value.push(errorMessage)
			}
		} else {
			errorsModal.value = errorsModal.value.filter(error => !error.includes('La fecha del examen profesional'))
		}
	} else {
		// Si alguna fecha no es válida, no mostrar el error de validación de exención
		errorsModal.value = errorsModal.value.filter(error => !error.includes('La fecha del examen profesional'))
	}
	degreeData.value.data.Expedicion.fechaExamenProfesional = fechaNormalizada
}

/**
 * @description Función que verifica si una fecha es válida
 * @param {Date} [date] - Fecha a verificar
 * @returns {boolean} true si la fecha es válida
 */
const verificarFecha = (date?: Date) => esFechaValida(date)

// Función principal para guardar el título
const guardarTitulo = async () => {
	// Validar que todos los campos requeridos estén completos
	validarServicioSocial()
	validarFormularioCompleto()

	// Validar fechas
	if (
		!validAllDates(
			degreeData.value.data.Carrera.fechaInicio,
			degreeData.value.data.Carrera.fechaTerminacion,
			degreeData.value.data.Antecedente.fechaTerminacion ?? '',
			degreeData.value.data.Expedicion.fechaExpedicion ?? '',
			degreeData.value.data.Expedicion.idModalidadTitulacion === EGraduationMode.POR_TESIS
				? (degreeData.value.data.Expedicion.fechaExamenProfesional ?? '')
				: (degreeData.value.data.Expedicion.fechaExencionExamenProfesional ?? '')
		)
	) {
		return
	}
	degreeData.value.data.Carrera.fechaInicio = new Date(degreeData.value.data.Carrera.fechaInicio).toISOString().split('T')[0]
	degreeData.value.data.Carrera.fechaTerminacion = new Date(degreeData.value.data.Carrera.fechaTerminacion).toISOString().split('T')[0]
	degreeData.value.data.Expedicion.fechaExpedicion = degreeData.value.data.Expedicion.fechaExpedicion ? new Date(degreeData.value.data.Expedicion.fechaExpedicion).toISOString().split('T')[0] : ''
	if (degreeData.value.data.Expedicion.idModalidadTitulacion !== EGraduationMode.POR_TESIS) {
		degreeData.value.data.Expedicion.fechaExencionExamenProfesional = degreeData.value.data.Expedicion.fechaExencionExamenProfesional
			? new Date(degreeData.value.data.Expedicion.fechaExencionExamenProfesional).toISOString().split('T')[0]
			: ''
		delete degreeData.value.data.Expedicion.fechaExamenProfesional
	} else {
		degreeData.value.data.Expedicion.fechaExamenProfesional = degreeData.value.data.Expedicion.fechaExamenProfesional
			? new Date(degreeData.value.data.Expedicion.fechaExamenProfesional).toISOString().split('T')[0]
			: ''
		delete degreeData.value.data.Expedicion.fechaExencionExamenProfesional
	}
	degreeData.value.data.Antecedente.fechaTerminacion = new Date(degreeData.value.data.Antecedente.fechaTerminacion).toISOString().split('T')[0]
	degreeData.value.data.Institucion.nombreInstitucion = await degreeService.getInstitutionName(props.currentInstitution)
	degreeData.value.data.Institucion.cveInstitucion = props.institution.cveInstitution
	degreeData.value.data.Profesionista.curp = degreeData.value.data.Profesionista.curp.toUpperCase()
	degreeData.value.data.Carrera.cveCarrera = getCveCarrera()
	if (!degreeData.value.data.Carrera.nombreCarrera.includes(EIdNivelEstudios[degreeData.value.data.Carrera.idNivelEstudios as unknown as keyof typeof EIdNivelEstudios])) {
		const institutionConfig = await getInstitutionConfiguration(props.currentInstitution)
		const conectorCarreraNivel = degreeData.value.data.Carrera.conectorCarreraNivel || institutionConfig?.degree?.conectorCarreraNivel
		if (
			!degreeData.value.data.Carrera.nombreCarrera.includes(conectorCarreraNivel) ||
			!degreeData.value.data.Carrera.nombreCarrera.includes(CIdNivelEstudios[EIdNivelEstudios[degreeData.value.data.Carrera.idNivelEstudios as unknown as keyof typeof EIdNivelEstudios]].toUpperCase())
		) {
			const nombreCarrera = degreeData.value.data.Carrera.nombreCarrera.toUpperCase()
			degreeData.value.data.Carrera.nombreCarrera = ''
			degreeData.value.data.Carrera.nombreCarrera =
				CIdNivelEstudios[EIdNivelEstudios[degreeData.value.data.Carrera.idNivelEstudios as unknown as keyof typeof EIdNivelEstudios]].toUpperCase() +
				' ' +
				(conectorCarreraNivel || 'EN') +
				' ' +
				nombreCarrera
		}
	}
	// Limpiar las firmas existentes antes de agregar las seleccionadas
	degreeData.value.data.FirmaResponsables = {}
	for (const responsable of selectedResponsables.value) {
		degreeData.value.data.FirmaResponsables[responsable] = { sign: false }
	}
	if (nivelEstudios.includes(degreeData.value.data.Carrera.idNivelEstudios)) {
		degreeData.value.data.Expedicion.cumplioServicioSocial = 0
	}
	if (degreeData.value.data.Expedicion.cumplioServicioSocial === 0) {
		degreeData.value.data.Expedicion.fundamentoLegalServicioSocial = NOMBRE_FUNDAMENTO_SERVICIO_SOCIAL[EFundamentoServicioSocial.NO_APLICA]
	}
	showModalLoading.value = true
	showModal.value = false
	try {
		await degreeService.saveData(degreeData.value, Number(props.currentInstitution), props.isTestMode, props.isEdit)
		// Emitir evento para notificar al componente padre
		if (props.isEdit) {
			emit('degree-updated', degreeData.value)
		} else {
			emit('degree-saved', degreeData.value)
		}
	} catch (error) {
		console.error('Error al guardar el título:', error)
		// Reabrir el modal en caso de error
		showModal.value = true
	} finally {
		showModalLoading.value = false
	}
}

// Watcher optimizado para degreeData completo
watch(
	() => degreeData.value,
	() => {
		try {
			validarFormularioCompleto()
		} catch (error) {
			console.error('Error en watcher de degreeData:', error)
		}
	},
	{ deep: true }
)

watch(
	() => degreeData.value?.data?.Profesionista?.curp,
	() => {
		getCurpError(degreeData.value.data.Profesionista.curp.toUpperCase())
	}
)
watch(
	() => degreeData.value?.data?.numeroControl,
	() => {
		getMatriculaError(degreeData.value.data.numeroControl)
	}
)

// Validar email
watch(
	() => degreeData.value?.data?.Profesionista?.correoElectronico,
	() => {
		getCorreoElectronicoError(degreeData.value.data.Profesionista.correoElectronico)
	}
)
watch(
	() => degreeData.value.data.Profesionista.nombre,
	() => {
		getCurpError(degreeData.value.data.Profesionista.curp.toUpperCase())
	}
)

watch(
	() => degreeData.value.data.Profesionista.primerApellido,
	() => {
		getCurpError(degreeData.value.data.Profesionista.curp.toUpperCase())
	}
)

watch(
	() => degreeData.value.data.Profesionista.segundoApellido,
	() => {
		getCurpError(degreeData.value.data.Profesionista.curp.toUpperCase())
	}
)

// Watcher para responsables - solo valida el formulario
watch(
	() => selectedResponsables.value,
	() => {
		validarFormularioCompleto()
	},
	{ deep: true }
)
const isNotEmpty = (value: any) => value !== '' && value !== null && value !== undefined
</script>

<template>
	<VaModal v-model="showModal" hide-default-actions noDismiss noEscDismiss noOutsideDismiss max-width="1500px" background-color="transparent">
		<VaCard max-w-4xl mx-auto>
			<!-- Header con gradiente -->
			<VaCardTitle :class="[props.isTestMode ? 'bg-gradient-paquete-test' : 'bg-gradient-paquete', 'rounded-t-xl']">
				<div class="row align-items-center">
					<div class="col-md-11 text-left">
						<VaIcon name="school" class="text-white" size="large" />
						<div>
							<h2 class="text-xl font-bold text-white m-0">
								{{ props.isEdit ? 'Editar Título' : 'Nuevo Título' }}
							</h2>
							<p class="text-sm text-blue-100 m-0 text-white">
								{{ institutionName }}
							</p>
						</div>
					</div>
					<div class="col-md-1 text-right ml2">
						<VaButton round icon="cancel" :color="!props.isTestMode ? '#1e3a8a' : '#7f1d1d'" type="button" @click="closeModal" style="color: white; font-weight: bold" />
					</div>
				</div>
			</VaCardTitle>

			<VaCardContent class="p-6">
				<!-- Tabs mejorados -->
				<VaTabs v-model="value" grow class="mb-6">
					<template #tabs>
						<VaTab v-for="tab in TABS" :key="tab.title" :name="tab.title" :disabled="tab.title === 'Título' && !isProfesionistaValid" class="flex items-center gap-2">
							<VaIcon :name="tab.icon" size="small" class="title-icon" />
							{{ tab.title }}
						</VaTab>
					</template>
				</VaTabs>

				<!-- Contenido de las pestañas -->
				<div v-if="currentTab?.title === 'Profesionista'" class="space-y-6">
					<!-- Sección: Datos del profesionista -->
					<div class="students-section">
						<div class="section-title">
							<VaIcon name="person" class="title-icon" />
							Datos del profesionista
						</div>
						<div class="students-grid">
							<div class="student-card">
								<div class="gap-3 mb-4">
									<div class="student-avatar mb-2">
										<VaIcon name="person" class="student-icon" />
									</div>
									<div>
										<h3 class="font-semibold text-lg m-0">Información Personal</h3>
										<p class="text-gray-600 m-0">Datos básicos del profesionista</p>
									</div>
								</div>
								<div class="grid grid-cols-1 md:grid-cols-2 gap-4">
									<div style="margin-bottom: 0">
										<VaInput style="width: 100%; margin-bottom: 0" v-model="degreeData.data.numeroControl" label="Matrícula (Identificador del profesionista)" required />
										<span v-if="!isNotEmpty(degreeData.data.numeroControl)" style="color: red; font-size: 10px; display: block; margin-top: 2px">Este campo es requerido</span>
									</div>
									<div style="margin-bottom: 0">
										<VaInput style="width: 100%; margin-bottom: 0" v-model="degreeData.data.Profesionista.curp" label="Curp" required />
										<span v-if="!isNotEmpty(degreeData.data.Profesionista.curp)" style="color: red; font-size: 10px; display: block; margin-top: 2px">Este campo es requerido</span>
									</div>
									<div style="margin-bottom: 0">
										<VaInput style="width: 100%; margin-bottom: 0" v-model="degreeData.data.Profesionista.nombre" label="Nombre" required />
										<span v-if="!isNotEmpty(degreeData.data.Profesionista.nombre)" style="color: red; font-size: 10px; display: block; margin-top: 2px">Este campo es requerido</span>
									</div>
									<div style="margin-bottom: 0">
										<VaInput style="width: 100%; margin-bottom: 0" v-model="degreeData.data.Profesionista.primerApellido" label="Primer apellido" required />
										<span v-if="!isNotEmpty(degreeData.data.Profesionista.primerApellido)" style="color: red; font-size: 10px; display: block; margin-top: 2px">Este campo es requerido</span>
									</div>
									<div style="margin-bottom: 0">
										<VaInput style="width: 100%; margin-bottom: 0" v-model="degreeData.data.Profesionista.segundoApellido" label="Segundo apellido" required />
									</div>
									<div style="margin-bottom: 0">
										<VaInput style="width: 100%; margin-bottom: 0" v-model="degreeData.data.Profesionista.correoElectronico" label="Email del alumno" type="email" required />
										<span v-if="!isNotEmpty(degreeData.data.Profesionista.correoElectronico)" style="color: red; font-size: 10px; display: block; margin-top: 2px">Este campo es requerido</span>
									</div>
								</div>
							</div>
						</div>
					</div>

					<!-- Sección: Antecedentes -->
					<div class="package-section">
						<div class="section-title">
							<VaIcon name="history" class="title-icon" />
							Antecedentes
						</div>
						<div class="package-grid">
							<div class="package-card">
								<div class="gap-3 mb-4">
									<div class="signer-avatar mb-2">
										<VaIcon name="school" class="signer-icon" />
									</div>
									<div>
										<h3 class="font-semibold text-lg m-0">Estudios Previos</h3>
										<p class="text-gray-600 m-0">Información académica anterior</p>
									</div>
								</div>
								<div style="margin-bottom: 0" class="mb-3">
									<VaSelect
										v-model="idTipoEstudioAntecedente"
										:options="opcionesTipoEstudioAntecedente"
										style="width: 100%"
										label="Tipo de antecedente"
										no-options-text="No se encontraron elementos"
										text-by="text"
										value-by="value"
										placeholder="Seleccione un tipo de antecedente" />
									<span v-if="!isNotEmpty(idTipoEstudioAntecedente)" style="color: red; font-size: 10px; display: block; margin-top: 2px">Este campo es requerido</span>
								</div>
								<div style="margin-bottom: 0" class="mb-3">
									<VaSelect
										v-model="idEntidadFederativa"
										:options="opcionesEntidadFederativa"
										style="width: 100%"
										label="Entidad"
										no-options-text="No se encontraron elementos"
										text-by="text"
										value-by="value"
										placeholder="Seleccione una entidad" />
									<span v-if="!isNotEmpty(idEntidadFederativa)" style="color: red; font-size: 10px; display: block; margin-top: 2px">Este campo es requerido</span>
								</div>

								<div class="package-card">
									<div class="gap-3 mb-4">
										<div class="signer-avatar mb-2">
											<VaIcon name="event" class="signer-icon" />
										</div>
										<div>
											<h3 class="font-semibold text-lg m-0">Fechas e Institución (Antecedente)</h3>
											<p class="text-gray-600 m-0">Cronología y procedencia</p>
										</div>
									</div>
									<div class="grid grid-cols-1">
										<div style="margin-bottom: 0; width: 100%" class="mb-3">
											<dateControl
												:value="degreeData.data.Antecedente.fechaTerminacion"
												:disabled="false"
												:defaultValue="true"
												label="Fecha de terminación"
												@update-Date="updateFechaTerminoAntecedente"
												style="width: 100%" />
											<span v-if="!isNotEmpty(degreeData.data.Antecedente.fechaTerminacion)" style="color: red; font-size: 10px; display: block; margin-top: 2px">Este campo es requerido</span>
										</div>
										<div style="margin-bottom: 0" class="mb-3">
											<VaInput v-model="degreeData.data.Antecedente.institucionProcedencia" style="width: 100%" label="Nombre de la institución antecesora" class="mb-3" />
											<span v-if="!isNotEmpty(degreeData.data.Antecedente.institucionProcedencia)" style="color: red; font-size: 10px; display: block; margin-top: 2px">Este campo es requerido</span>
										</div>
										<div style="margin-bottom: 0" class="mb-3">
											<VaInput v-model="degreeData.data.Antecedente.noCedula" style="width: 100%" label="Número de cédula" :rules="[validarCedula]" />
											<span
												v-if="
													!isNotEmpty(degreeData.data.Antecedente.noCedula) &&
													(idTipoEstudioAntecedente === ETipoEstudioAntecedente.LICENCIATURA || idTipoEstudioAntecedente === ETipoEstudioAntecedente.TECNICO_SUPERIOR_UNIVERSITARIO)
												"
												style="color: red; font-size: 10px; display: block; margin-top: 2px"
												>Este campo es requerido</span
											>
										</div>
									</div>
								</div>
							</div>
						</div>

						<!-- Sección: Carrera -->
						<div class="package-section">
							<div class="section-title">
								<VaIcon name="collections_bookmark" class="title-icon" />
								Carrera
							</div>
							<div class="package-grid">
								<div class="package-card">
									<div class="gap-3 mb-4">
										<div class="signer-avatar mb-2">
											<VaIcon name="school" class="signer-icon" />
										</div>
										<div>
											<h3 class="font-semibold text-lg m-0">Carrera y RVOE</h3>
											<p class="text-gray-600 m-0">Información académica actual</p>
										</div>
									</div>
									<div style="margin-bottom: 0" class="mb-3">
										<VaSelect
											v-model="idCarrera"
											style="width: 100%"
											:options="carreras"
											value-by="idCarrera"
											text-by="nombreCarrera"
											label="Carrera"
											no-options-text="No se encontraron elementos"
											placeholder="Seleccione una carrera"
											class="mb-3" />
										<span v-if="!isNotEmpty(idCarrera)" style="color: red; font-size: 10px; display: block; margin-top: 2px">Este campo es requerido</span>
									</div>
									<div style="margin-bottom: 0" class="mb-3">
										<VaSelect
											v-model="degreeData.data.Carrera.numeroRvoe"
											style="width: 100%"
											label="Número de RVOE"
											:options="revoCurrentCarrer"
											value-by="value"
											text-by="text"
											no-options-text="No se encontraron elementos"
											placeholder="Seleccione un RVOE" />
										<span v-if="!isNotEmpty(degreeData.data.Carrera.numeroRvoe)" style="color: red; font-size: 10px; display: block; margin-top: 2px">Este campo es requerido</span>
									</div>
								</div>

								<div class="package-card">
									<div class="gap-3 mb-4">
										<div class="signer-avatar mb-2">
											<VaIcon name="event" class="signer-icon" />
										</div>
										<div>
											<h3 class="font-semibold text-lg m-0">Período de Estudios</h3>
											<p class="text-gray-600 m-0">Fechas de inicio y terminación</p>
										</div>
									</div>
									<div class="grid grid-cols-1 md:grid-cols-2 gap-4">
										<div style="margin-bottom: 0; width: 100%" class="mb-3">
											<dateControl style="width: 100%" :value="degreeData.data.Carrera.fechaInicio" :disabled="false" :defaultValue="true" label="Fecha de inicio" @update-Date="updateFechaInicio" />
											<span v-if="!isNotEmpty(degreeData.data.Carrera.fechaInicio)" style="color: red; font-size: 10px; display: block; margin-top: 2px">Este campo es requerido</span>
										</div>
										<div style="margin-bottom: 0; width: 100%" class="mb-3">
											<dateControl
												style="width: 100%"
												:value="degreeData.data.Carrera.fechaTerminacion"
												:disabled="false"
												:defaultValue="true"
												label="Fecha de terminación"
												@update-Date="updateFechaTermino" />
											<span v-if="!isNotEmpty(degreeData.data.Carrera.fechaTerminacion)" style="color: red; font-size: 10px; display: block; margin-top: 2px">Este campo es requerido</span>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div v-if="currentTab?.title === 'Título'" class="space-y-6">
					<!-- Sección: Datos de Expedición -->
					<div class="package-section">
						<div class="section-title">
							<VaIcon name="event" class="title-icon" />
							Datos de Expedición
						</div>
						<div class="package-grid">
							<div class="package-card">
								<div class="gap-3 mb-4">
									<div class="signer-avatar mb-2">
										<VaIcon name="badge" class="signer-icon" />
									</div>
									<div>
										<h3 class="font-semibold text-lg m-0">Título y Modalidad</h3>
										<p class="text-gray-600 m-0">Configuración del título</p>
									</div>
								</div>
								<div class="grid grid-cols-1">
									<div style="margin-bottom: 0; width: 100%" class="mb-3">
										<dateControl
											:value="degreeData.data.Expedicion.fechaExpedicion"
											:disabled="false"
											:defaultValue="true"
											label="Fecha de expedición"
											@update-Date="updateFechaExpedicion"
											:verificar-fecha="verificarFecha"
											style="width: 100%" />
										<span v-if="!isNotEmpty(degreeData.data.Expedicion.fechaExpedicion)" style="color: red; font-size: 10px; display: block; margin-top: 2px">Este campo es requerido</span>
									</div>
									<div style="margin-bottom: 0; width: 100%" class="mb-3">
										<VaSelect
											v-model="idModalidadTitulacion"
											:options="opcionesModalidadTitulacion"
											style="width: 100%"
											label="Modalidad de titulación"
											no-options-text="No se encontraron elementos"
											text-by="text"
											value-by="value"
											placeholder="Seleccione una modalidad de titulación" />
										<span v-if="!isNotEmpty(idModalidadTitulacion)" style="color: red; font-size: 10px; display: block; margin-top: 2px">Este campo es requerido</span>
									</div>
								</div>
							</div>

							<div class="package-card">
								<div class="gap-3 mb-4">
									<div class="signer-avatar mb-2">
										<VaIcon name="location_on" class="signer-icon" />
									</div>
									<div>
										<h3 class="font-semibold text-lg m-0">Ubicación y Examen</h3>
										<p class="text-gray-600 m-0">Lugar y fechas importantes</p>
									</div>
								</div>
								<div class="grid grid-cols-1">
									<div style="margin-bottom: 0; width: 100%" class="mb-3">
										<VaSelect
											v-model="idEntidadFederativaExpedicion"
											:options="opcionesEntidadFederativa"
											style="width: 100%"
											label="Entidad"
											no-options-text="No se encontraron elementos"
											text-by="text"
											value-by="value"
											placeholder="Seleccione una entidad"
											class="mb-3" />
										<span v-if="!isNotEmpty(idEntidadFederativaExpedicion)" style="color: red; font-size: 10px; display: block; margin-top: 2px">Este campo es requerido</span>
									</div>
									<div style="margin-bottom: 0; width: 100%" class="mb-3" v-if="degreeData.data.Expedicion.idModalidadTitulacion === EGraduationMode.POR_TESIS">
										<dateControl
											:value="degreeData.data.Expedicion.fechaExamenProfesional"
											:disabled="false"
											:default-value="true"
											label="Fecha de examen profesional"
											@update-Date="updateFechaExamenProfesional"
											style="width: 100%" />
										<span v-if="!isNotEmpty(degreeData.data.Expedicion.fechaExamenProfesional)" style="color: red; font-size: 10px; display: block; margin-top: 2px">Este campo es requerido</span>
									</div>
									<div style="margin-bottom: 0; width: 100%" class="mb-3" v-else>
										<dateControl
											:value="degreeData.data.Expedicion.fechaExencionExamenProfesional"
											:disabled="false"
											:default-value="true"
											label="Fecha de exención del examen profesional"
											@update-Date="updateFechaExencionExamenProfesional"
											style="width: 100%" />
										<span v-if="!isNotEmpty(degreeData.data.Expedicion.fechaExencionExamenProfesional)" style="color: red; font-size: 10px; display: block; margin-top: 2px"
											>Este campo es requerido</span
										>
									</div>
								</div>
							</div>
						</div>
					</div>

					<!-- Sección: Servicio Social -->
					<div v-if="!nivelEstudios.includes(degreeData.data.Carrera.idNivelEstudios)" class="package-section">
						<div class="section-title">
							<VaIcon name="volunteer_activism" class="title-icon" />
							Servicio Social
						</div>
						<div class="package-card">
							<div class="gap-3 mb-4">
								<div class="signer-avatar mb-2">
									<VaIcon name="help" class="signer-icon" />
								</div>
								<div>
									<h3 class="font-semibold text-lg m-0">Cumplimiento y Fundamento</h3>
									<p class="text-gray-600 m-0">Información del servicio social</p>
								</div>
							</div>
							<div class="grid grid-cols-1 md:grid-cols-2 gap-4">
								<div>
									<VaBadge text="Cumplió con Servicio Social" color="primary" class="mb-2" />
									<VaRadio v-model="degreeData.data.Expedicion.cumplioServicioSocial" :options="opcionesServicioSocial" value-by="value" text-by="label" />
								</div>
								<div>
									<VaSelect
										v-model="idFundamentoLegalServicioSocial"
										:options="opcionesFundamentoLegalServicioSocial"
										style="width: 100%"
										label="Fundamento legal de Servicio Social"
										no-options-text="No se encontraron elementos"
										text-by="text"
										value-by="value"
										placeholder="Seleccione un fundamento legal de servicio social" />
								</div>
							</div>
						</div>
					</div>

					<!-- Sección: Responsables -->
					<div class="signers-section">
						<div class="section-title">
							<VaIcon name="groups" class="title-icon" />
							Responsables a firmar
							{{ selectedResponsables }}
						</div>
						<div class="package-card">
							<div class="table-container">
								<div class="table-body-scroll">
									<table class="styled-table w100">
										<thead>
											<tr>
												<th class="info-labelP bg-primary-color bold" scope="col" style="color: white; width: 50px">
													<VaCheckbox
														:model-value="selectedResponsables.length === opcionesResponsable.length"
														@update:model-value="
															(checked: boolean) => {
																if (checked) {
																	selectedResponsables = opcionesResponsable.map(r => r.value)
																} else {
																	selectedResponsables = []
																}
															}
														" />
												</th>
												<th class="info-label bg-primary-color bold" scope="col" style="color: white">Nombre</th>
												<th class="info-label bg-primary-color bold" scope="col" style="color: white">CURP</th>
											</tr>
										</thead>
										<tbody>
											<tr v-for="responsable in opcionesResponsable" :key="responsable.value" class="table-hover">
												<td class="info-value" style="font-size: 10px">
													<VaCheckbox :model-value="isResponsableSelected(responsable.value)" @update:model-value="() => toggleResponsable(responsable.value)" />
												</td>
												<td class="info-value" style="font-size: 10px">
													<VaIcon name="user" color="primary" style="margin-right: 10px" />
													{{ responsable.text }}
												</td>
												<td class="info-value" style="font-size: 10px">{{ responsable.value }}</td>
											</tr>
										</tbody>
									</table>
								</div>
							</div>
							<div class="mt-3">
								<small class="text-muted"> Responsables seleccionados: {{ selectedResponsables.length }} de {{ opcionesResponsable.length }} </small>
							</div>
						</div>
					</div>
				</div>
			</VaCardContent>

			<!-- Footer con botones -->
			<VaCardActions class="footer-actions" align="right">
				<VaButton color="secondary" class="btn btn-secondary" @click="closeModal">
					<VaIcon name="cancel" class="mr-2" />
					Cancelar
				</VaButton>
				<VaButton color="primary" class="btn btn-primary" :disabled="errorsModal.length > 0" @click="guardarTitulo">
					<VaIcon name="save" class="mr-2" />
					Guardar
				</VaButton>
			</VaCardActions>
		</VaCard>

		<!-- Alertas de error -->
		<div v-if="errorsModal.length > 0" class="status-alert">
			<VaAlert color="danger" class="flex items-center justify-center w-full">
				<VaIcon name="error" class="mr-2" />
				<span class="text-base">
					<ul class="list-none p-0 m-0">
						<li v-for="error in errorsModal" :key="error" class="mb-1">{{ error }}</li>
					</ul>
				</span>
			</VaAlert>
		</div>
	</VaModal>

	<VaModal v-model="showModalLoading" hide-default-actions>
		<VaCard square outlined>
			<VaCardContent>
				<span class="text-secondary" style="font-size: 18px; font-weight: bold">Cargando... </span>
				<VaProgressCircle :size="100" :indeterminate="true" color="primary" />
			</VaCardContent>
		</VaCard>
	</VaModal>
</template>

<style lang="css" scoped>
.rounded-t-xl {
	border-top-left-radius: 0.75rem;
	border-top-right-radius: 0.75rem;
}

/* Títulos de sección */
.section-title {
	font-size: 1.25rem;
	font-weight: 600;
	color: #1a2a3a;
	margin-bottom: 1.5rem;
	padding-bottom: 0.75rem;
	border-bottom: 2px solid #e9ecef;
	display: flex;
	align-items: center;
	gap: 0.5rem;
}

.title-icon {
	color: #007bff;
	font-size: 1.25rem;
}

/* Avatares */
.signer-avatar,
.student-avatar {
	width: 25px;
	height: 25px;
	border-radius: 50%;
	background: linear-gradient(135deg, #6ab0ff 0%, #0873c4 100%);
	display: flex;
	align-items: center;
	justify-content: center;
}

.signer-icon,
.student-icon {
	color: white;
	font-size: 16px;
	line-height: 1;
}

/* Tarjetas */
.package-card,
.student-card {
	background: white;
	border-radius: 12px;
	border: 2px solid #e9ecef;
	padding: 1.25rem;
	transition: all 0.3s ease;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
	animation: fadeIn 0.5s ease-in-out;
}

.package-card:hover,
.student-card:hover {
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
	transform: translateY(-2px);
}

/* Grids */
.package-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
	gap: 1rem;
}

.students-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
	gap: 1rem;
}

/* Botones */
.btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	padding: 0.5rem 1rem;
	border-radius: 8px;
	font-weight: 600;
	font-size: 0.875rem;
	transition: all 0.3s ease;
	border: none;
	cursor: pointer;
	text-decoration: none;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.btn-primary {
	background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
	color: white;
}

.btn-primary:hover {
	background: linear-gradient(135deg, #0056b3 0%, #004085 100%);
	transform: translateY(-1px);
	box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

/* Animaciones */
@keyframes fadeIn {
	from {
		opacity: 0;
		transform: scale(0.8);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
}

/* Tabla mejorada */
.styled-table {
	width: 100%;
	border-collapse: collapse;
	font-family: Arial, sans-serif;
	background-color: #f9f9f9;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
	overflow: hidden;
	border: 2px solid #1e3a8a !important;
	border-radius: 8px;
}

.styled-table thead tr {
	text-align: left;
}

.styled-table th,
.styled-table td {
	padding: 12px 15px;
	border: 1px solid #dddddd;
}

.styled-table tbody tr {
	border-bottom: 1px solid #dddddd;
	transition: background-color 0.2s ease;
}

.styled-table tbody tr:hover {
	background-color: #f0f8ff;
}

.styled-table tbody tr:nth-of-type(even) {
	background-color: #f3f3f3;
}

.styled-table tbody tr:nth-of-type(even):hover {
	background-color: #e6f3ff;
}

.styled-table tbody tr:last-of-type {
	border-bottom: 2px solid #1e3a8a !important;
}

.bg-primary-color {
	background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
}

.table-container {
	position: relative;
	border-radius: 8px;
	overflow: hidden;
}

.table-body-scroll {
	max-height: 300px;
	overflow-y: auto;
	border: 2px solid #1e3a8a;
	border-top: none;
	border-radius: 0 0 8px 8px;
}

.table-body-scroll::-webkit-scrollbar {
	width: 8px;
}

.table-body-scroll::-webkit-scrollbar-track {
	background: #f1f1f1;
	border-radius: 4px;
}

.table-body-scroll::-webkit-scrollbar-thumb {
	background: #888;
	border-radius: 4px;
}

.table-body-scroll::-webkit-scrollbar-thumb:hover {
	background: #555;
}

/* Responsive */
@media (max-width: 768px) {
	.package-grid,
	.students-grid {
		grid-template-columns: 1fr;
	}

	.package-card,
	.student-card {
		padding: 1rem;
	}

	.section-title {
		font-size: 1.125rem;
	}

	.signer-avatar,
	.student-avatar {
		width: 20px;
		height: 20px;
		font-size: 16px;
		line-height: 1;
	}
}

/* Utilidades */
.space-y-6 > * + * {
	margin-top: 1.5rem;
}

.mb-4 {
	margin-bottom: 1rem;
}

.mb-6 {
	margin-bottom: 0.5rem;
}

.mr-2 {
	margin-right: 0.5rem;
}

.gap-2 {
	gap: 0.5rem;
}

.gap-3 {
	gap: 0.75rem;
}

.gap-4 {
	gap: 1rem;
}

.flex {
	display: flex;
}

.items-center {
	align-items: center;
}

.text-white {
	color: white;
}

.text-gray-600 {
	color: #6c757d;
}

.font-semibold {
	font-weight: 600;
}

.font-bold {
	font-weight: 700;
}

.text-lg {
	font-size: 1.125rem;
}

.text-xl {
	font-size: 1.25rem;
}

.text-sm {
	font-size: 0.875rem;
}

.m-0 {
	margin: 0;
}

.p-0 {
	padding: 0;
}

.list-none {
	list-style: none;
}

.bg-gradient-paquete {
	background: linear-gradient(90deg, #2563eb 0%, #1e3a8a 100%);
}

.bg-gradient-paquete-test {
	background: linear-gradient(90deg, #dc2626 0%, #7f1d1d 100%);
}

/* Footer actions */
.footer-actions {
	padding: 0.5rem;
	background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
	border-top: 2px solid #dee2e6;
	border-radius: 0 0 12px 12px;
}

.footer-actions .flex {
	width: 100%;
}

/* Botones mejorados */
.btn-secondary {
	background: linear-gradient(135deg, #6c757d 0%, #495057 100%);
	color: white;
	border: 2px solid #6c757d;
}

.btn-secondary:hover {
	background: linear-gradient(135deg, #495057 0%, #343a40 100%);
	transform: translateY(-1px);
	box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
	border-color: #495057;
}

.btn-primary:hover:not(:disabled) {
	background: linear-gradient(135deg, #0056b3 0%, #004085 100%);
	transform: translateY(-1px);
	box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
	border-color: #0056b3;
}

.btn-primary:disabled {
	background: linear-gradient(135deg, #adb5bd 0%, #6c757d 100%);
	border-color: #adb5bd;
	cursor: not-allowed;
	transform: none;
	box-shadow: none;
}

/* Status alert */
.status-alert {
	position: fixed;
	top: 20px;
	left: 50%;
	transform: translateX(-50%);
	z-index: 9999;
	max-width: 600px;
	width: 90%;
}
</style>
